import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  CardTitle
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { MdInfo } from "react-icons/md";
import classNames from "classnames";
import { getActiveColorStyle } from "../../utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import Select from "../../components/Select";
import moment from "moment";
import { useGetChannels } from "../../api/channels/service";
import { useGetTaxInfos, useGenerateInventoryTaxReport } from "../../api/reports/service";
import CustomDatePicker from "../../components/CustomDatePicker";
import { formatPriceWithDollar, toNumber } from "../../utils/currency";
import { useNavigate } from "react-router-dom";

const TxSellerFinancedSalesTaxForm = ({ activeColor = "blue", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const isTheme2 = shellThemeVariant === "2";
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".seller-financed-sales-tax-page.seller-financed-sales-tax-page--theme2",
            fieldsSelector: ".seller-financed-sales-tax-form-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2
    ? { height: "35px", fontSize: "14px", paddingRight: "10px", textAlign: "right" }
    : { height: "35px", fontSize: "13px", paddingRight: "10px", textAlign: "right" };
  const dateInputStyle = isTheme2
    ? { height: "35px", fontSize: "14px" }
    : { height: "35px", fontSize: "13px" };
  const [isLightTheme, setIsLightTheme] = useState(true);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const textColor = isLightTheme ? "#1d253b" : "#ffffff";
  const labelColor = isLightTheme ? "#525f7f" : "#rgba(255, 255, 255, 0.8)";
  const cardBg = isLightTheme ? "#ffffff" : "#27293d";
  const subTextColor = isLightTheme ? "#525f7f" : "rgba(255, 255, 255, 0.6)";

  // Fetch tax infos
  const { data: taxInfosData, isLoading: taxInfosLoading } = useGetTaxInfos();
  const generateReportMutation = useGenerateInventoryTaxReport();

  const [pdfUrl, setPdfUrl] = useState(null);

  const [formData, setFormData] = useState(() => {
    return {
      beginningDate: "",
      endingDate: "",
      salesTaxCompany: "",
      totalPrepayments: "$0.00",
      filingOnTime: "",
      penaltyInterestAmount: "$0.00",
      completeTheForm: ""
    };
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleDateChange = (name, val) => {
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
  };

  const handleSelectChange = (name, option) => {
    setFormData(prev => ({
      ...prev,
      [name]: option ? option.value : ""
    }));
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    // Keep only numbers and dots
    const raw = value.replace(/[^\d.]/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: raw
    }));
  };

  const handlePriceBlur = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: formatPriceWithDollar(value)
    }));
  };

  const handlePriceFocus = (e) => {
    const { name, value } = e.target;
    const raw = value.toString().replace(/[^\d.]/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: raw
    }));
  };

  const yesNoOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const completeFormOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const salesTaxCompanyOptions = useMemo(() => {
    const results = taxInfosData?.results || taxInfosData;
    if (results && Array.isArray(results) && results.length > 0) {
      return results.map(item => ({
        value: item.documentId,
        label: item.tax_number
      }));
    }

    // Fallback if no data
    return [];
  }, [taxInfosData]);

  useEffect(() => {
    if (salesTaxCompanyOptions.length > 0 && !formData.salesTaxCompany) {
      setFormData(prev => ({ ...prev, salesTaxCompany: salesTaxCompanyOptions[0].value }));
    }
  }, [salesTaxCompanyOptions, formData.salesTaxCompany]);

  const handleGenerate = async () => {
    try {
      if (!formData.beginningDate || !formData.endingDate) {
        return;
      }

      const payload = {
        beginning_date: moment(formData.beginningDate).format("YYYY-MM-DD"),
        ending_date: moment(formData.endingDate).format("YYYY-MM-DD"),
        sales_tax_company_id: formData.salesTaxCompany,
        total_prepayments: toNumber(formData.totalPrepayments),
        filing_on_time: formData.filingOnTime,
        penalty_interest_amount: toNumber(formData.penaltyInterestAmount),
        complete_the_form: formData.completeTheForm,
        statementType: "Sales Tax Form"
      };

      const result = await generateReportMutation.mutateAsync(payload);
      if (result) {
        const blobUrl = URL.createObjectURL(new Blob([result], { type: 'application/pdf' }));
        setPdfUrl(blobUrl);
      }
    } catch (e) {
      console.error("Failed to generate report", e);
    }
  };

  const isSubmitDisabled = !formData.beginningDate || !formData.endingDate || !formData.salesTaxCompany || generateReportMutation.isPending;

  const contentClassName = classNames("content", "seller-financed-sales-tax-page", {
    "seller-financed-sales-tax-page--theme2": isTheme2,
    "theme2-admin-content-compact": isTheme2,
    "theme2-standard-tax-reports-inset": isTheme2,
  });

  if (pdfUrl) {
    return (
      <div className={contentClassName}>
        {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
        <Card className="shadow-sm" style={{ border: "none", overflow: "hidden" }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '15px 25px', backgroundColor: '#fcfcfc', borderBottom: '1px solid #eee' }}>
            <Button
              size="sm"
              color="secondary"
              onClick={() => setPdfUrl(null)}
              className="mb-0"
              style={{ padding: "6px 12px", display: 'flex', alignItems: 'center', boxShadow: 'none' }}
            >
              <i className="tim-icons icon-minimal-left" style={{ marginRight: "6px", fontSize: "10px" }} />
              {t('common.back') || 'Back'}
            </Button>
            <h4 style={{ margin: 0, fontWeight: "700", fontSize: "14px", color: "#1d253b", letterSpacing: "0.5px", marginLeft: "20px", textTransform: "uppercase" }}>
              DOCUMENT VIEWER
            </h4>
          </div>
          <CardBody style={{ padding: "15px 25px", height: "82vh", minHeight: "600px", backgroundColor: "#fcfcfc" }}>
            <iframe src={pdfUrl} width="100%" height="100%" style={{ border: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }} title="Seller-Financed Tax Form PDF" />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className={contentClassName}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <style>{`
        .rdtPicker td.rdtToday {
          background-color: rgba(0, 0, 0, 0.05) !important;
          font-weight: 700 !important;
          position: relative;
        }
        .rdtPicker td.rdtToday:after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          background: #2dce89;
          border-radius: 50%;
        }
      `}</style>
      <Card className="mb-4 shadow-sm" style={{ border: "none", backgroundColor: cardBg }}>
        <CardBody
          className={classNames({ "seller-financed-sales-tax-form-fields": isTheme2 })}
          style={{ padding: "10px 15px" }}
        >
          {taxInfosLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{ color: activeColorStyle.borderColor }}
              >
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <CardTitle tag="h4" style={{ margin: 0, marginBottom: 20 }}>
                TX - MOTOR VEHICLE SELLER-FINANCED SALES TAX FORM
              </CardTitle>

              <Row>
                <Col md="6" className="mb-2">
                  <FormGroup>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "2px", color: labelColor }}>Beginning Date:</Label>
                    <CustomDatePicker
                      timeFormat={false}
                      value={formData.beginningDate}
                      onChange={(val) => handleDateChange("beginningDate", val)}
                      inputProps={{
                        style: dateInputStyle,
                        className: "form-control",
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-2">
                  <FormGroup>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "2px", color: labelColor }}>Ending Date:</Label>
                    <CustomDatePicker
                      timeFormat={false}
                      value={formData.endingDate}
                      onChange={(val) => handleDateChange("endingDate", val)}
                      inputProps={{
                        style: dateInputStyle,
                        className: "form-control",
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6" className="mb-2">
                  <FormGroup>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "2px", color: labelColor }}>Sales Tax Company You Are Filing?</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      options={salesTaxCompanyOptions}
                      isLoading={taxInfosLoading}
                      value={salesTaxCompanyOptions.find(o => o.value === formData.salesTaxCompany) || null}
                      onChange={(option) => handleSelectChange("salesTaxCompany", option)}
                    />
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-2">
                  <FormGroup>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "2px", color: labelColor }}>Total Amount Of Prepayments <span style={{ fontWeight: "400", fontSize: "10px", fontStyle: "italic" }}>(if applicable)</span></Label>
                    <Input
                      type="text"
                      name="totalPrepayments"
                      className="form-control"
                      value={formData.totalPrepayments}
                      onChange={handlePriceChange}
                      onFocus={handlePriceFocus}
                      onBlur={handlePriceBlur}
                      style={inputStyle}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6" className="mb-2">
                  <FormGroup>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "2px", color: labelColor }}>Are you filing and paying on time?</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      options={yesNoOptions}
                      value={yesNoOptions.find(o => o.value === formData.filingOnTime) || null}
                      onChange={(option) => handleSelectChange("filingOnTime", option)}
                    />
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-2">
                  <FormGroup>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "2px", color: labelColor }}>If not, enter the Penalty/Interest amount:</Label>
                    <Input
                      type="text"
                      name="penaltyInterestAmount"
                      className="form-control"
                      value={formData.penaltyInterestAmount}
                      onChange={handlePriceChange}
                      onFocus={handlePriceFocus}
                      onBlur={handlePriceBlur}
                      style={inputStyle}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6" className="mb-2">
                  <FormGroup>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "2px", color: labelColor, display: "flex", alignItems: "center" }}>
                      Complete The Form?
                      <MdInfo size={14} style={{ marginLeft: "6px", color: activeColorStyle.borderColor, cursor: "help" }} id="completeFormInfo" />
                    </Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      options={completeFormOptions}
                      value={completeFormOptions.find(o => o.value === formData.completeTheForm) || null}
                      onChange={(option) => handleSelectChange("completeTheForm", option)}
                    />
                  </FormGroup>
                </Col>
                <Col md="6" className="d-flex align-items-end justify-content-end pb-3">
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => navigate(-1)}
                    style={{
                      padding: "8px 25px",
                      fontSize: "12px",
                      margin: "0 15px 0 0",
                      boxShadow: "none",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={isSubmitDisabled}
                    style={{
                      background: "#2dce89",
                      borderColor: "#2dce89",
                      color: "#fff",
                      fontWeight: "700",
                      fontSize: "12px",
                      padding: "8px 25px",
                      letterSpacing: "0.5px",
                      margin: 0,
                      backgroundImage: "none",
                      boxShadow: "none",
                      opacity: isSubmitDisabled ? 0.6 : 1,
                      cursor: isSubmitDisabled ? "not-allowed" : "pointer"
                    }}
                    onClick={handleGenerate}
                  >
                    {generateReportMutation.isPending ? "GENERATING..." : "SUBMIT"}
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default TxSellerFinancedSalesTaxForm;
